import { EndState } from '../interfaces/endPageMessage';

export const END_STATE: { [K in EndState]: K } = {
  EVENT_FULL: 'EVENT_FULL',
  WAITLISTED: 'WAITLISTED',
  DENIED: 'DENIED',
  SCREENING_CLOSED: 'SCREENING_CLOSED',
  INDUSTRY_BANNED: 'INDUSTRY_BANNED',
  DEFAULT: 'DEFAULT'
};
export const END_STATES: EndState[] = Object.keys(END_STATE) as EndState[];

export const FALLBACK_END_STATE: EndState = END_STATE.DEFAULT;

export const REGISTRATION_STATE = {
  DENIED: 'Denied',
  WAITING: 'Waiting',
  NO_TO_WAITING: 'NoToWaiting',
  FULL: 'Full'
} as const;

const STATUS_CANCELLED = 4;
const STATUS_CLOSED = 5;

export interface EndStateContext {
  screeningStatus?: string | number;
  confirmedCount?: number;
  rsvpMax?: number;
  registrationState?: string;
  industryBanned?: boolean;
  registrationEnded?: boolean;
  fullError?: boolean;
}

export const isAtCapacity = (ctx: EndStateContext): boolean =>
  ctx.rsvpMax != null && +ctx.rsvpMax > 0 && ctx.confirmedCount != null && +ctx.confirmedCount >= +ctx.rsvpMax;

/**
 * Screening.status 5 (Closed) is set automatically once confirmedCount reaches rsvpMax,
 * so a closed screening at capacity is "full", not "closed". Only cancelled screenings and
 * screenings closed below capacity count as closed.
 */
export const isScreeningClosed = (ctx: EndStateContext): boolean => {
  const status = ctx.screeningStatus == null ? NaN : +ctx.screeningStatus;
  if (status === STATUS_CANCELLED) { return true; }
  return status === STATUS_CLOSED && !isAtCapacity(ctx);
};

/**
 * The single place that maps (event status + respondent status) to an end state.
 */
export const resolveEndState = (ctx: EndStateContext = {}): EndState => {
  if (ctx.registrationEnded || isScreeningClosed(ctx)) { return END_STATE.SCREENING_CLOSED; }
  if (ctx.industryBanned) { return END_STATE.INDUSTRY_BANNED; }
  if (ctx.registrationState === REGISTRATION_STATE.DENIED) { return END_STATE.DENIED; }
  if (ctx.registrationState === REGISTRATION_STATE.WAITING || ctx.registrationState === REGISTRATION_STATE.NO_TO_WAITING) { return END_STATE.WAITLISTED; }
  if (ctx.fullError || ctx.registrationState === REGISTRATION_STATE.FULL || isAtCapacity(ctx)) { return END_STATE.EVENT_FULL; }
  return FALLBACK_END_STATE;
};

export const isEndState = (value: any): value is EndState => END_STATES.indexOf(value) >= 0;
